import { jsx, jsxs } from 'react/jsx-runtime';
import cx from 'clsx';
import { useRef, useState, useMemo, useEffect } from 'react';
import { u as useCssVariable } from './hook-C_LtjIcQ.js';
import { u as useDocumentEvent, D as DOCUMENT_EVENT_NAME_CLICK } from './hook-D51BbGUy.js';
import { A as AbsolutePositionAligner } from './component-Drqwk5-1.js';
import { F as FixedPositionAligner } from './component-gOY2cmyW.js';
import { I as IosArrowDown } from './component-DJBRQOIp.js';
import { I as IosArrowUp } from './component-EU_5qzcL.js';
import { I as Input } from './component-DchS1oBD.js';
import { M as Menu } from './component-PCp5abNO.js';
import { M as MenuItem } from './component-BcM2NOEt.js';
import { alignTopXBegin, alignTopXEnd, alignBottomXBegin, alignBottomXEnd } from '../utils/position/absolute/index.js';
import { alignTopXBegin as alignTopXBegin$1, alignTopXEnd as alignTopXEnd$1, alignBottomXBegin as alignBottomXBegin$1, alignBottomXEnd as alignBottomXEnd$1 } from '../utils/position/fixed/index.js';
import { e as MENU_BORDER_RADIUS_MEDIUM, d as MENU_BORDER_RADIUS_LARGE, c as MENU_BORDER_RADIUS_EXTRA_LARGE, j as MENU_CONTAINER_POSITION_TOP, i as MENU_CONTAINER_POSITION_BOTTOM, p as MENU_LAYER_POSITION_ABSOLUTE, q as MENU_LAYER_POSITION_FIXED, a as MENU_ALIGNMENT_CONTAINER_LAYER_NONE, M as MENU_ALIGNMENT_CONTAINER_LAYER_BASE, b as MENU_ALIGNMENT_CONTAINER_LAYER_POPOVER, n as MENU_ITEM_TEXT_OVERFLOW_NOWRAP, o as MENU_ITEM_TEXT_OVERFLOW_WRAP, m as MENU_ITEM_TEXT_OVERFLOW_CLAMP, v as SELECT_SIZE_SM, u as SELECT_SIZE_MD, g as MENU_CONTAINER_ALIGNMENT_LEFT, h as MENU_CONTAINER_ALIGNMENT_RIGHT, w as styles, x as SELECT_SEARCH_MODE_INPUT_PLACEHOLDER_DEFAULT, l as MENU_FIT_SELECT, S as SELECT_ARROW_DOWN_ICON_SIZE, f as MENU_CLOSE_DELAY_MSEC_DEFAULT, s as SELECT_PLACEHOLDER_CONFIG_TEXT_DEFAULT, t as SELECT_PLACEHOLDER_CONFIG_VALUE_DEFAULT, r as SELECT_PLACEHOLDER_CONFIG_IS_OPTION_HIDDEN_DEFAULT } from './styles.module-dDsRFEAR.js';

const absolutePositionAligmentConfigMap = {
    [MENU_CONTAINER_POSITION_TOP]: {
        [MENU_CONTAINER_ALIGNMENT_LEFT]: {
            rectCalculator: alignTopXBegin,
        },
        [MENU_CONTAINER_ALIGNMENT_RIGHT]: {
            rectCalculator: alignTopXEnd,
        },
    },
    [MENU_CONTAINER_POSITION_BOTTOM]: {
        [MENU_CONTAINER_ALIGNMENT_LEFT]: {
            rectCalculator: alignBottomXBegin,
        },
        [MENU_CONTAINER_ALIGNMENT_RIGHT]: {
            rectCalculator: alignBottomXEnd,
        },
    },
};
const fixedPositionAligmentConfigMap = {
    [MENU_CONTAINER_POSITION_TOP]: {
        [MENU_CONTAINER_ALIGNMENT_LEFT]: {
            rectCalculator: alignTopXBegin$1,
        },
        [MENU_CONTAINER_ALIGNMENT_RIGHT]: {
            rectCalculator: alignTopXEnd$1,
        },
    },
    [MENU_CONTAINER_POSITION_BOTTOM]: {
        [MENU_CONTAINER_ALIGNMENT_LEFT]: {
            rectCalculator: alignBottomXBegin$1,
        },
        [MENU_CONTAINER_ALIGNMENT_RIGHT]: {
            rectCalculator: alignBottomXEnd$1,
        },
    },
};
const positionAligmentConfigMap = {
    [MENU_LAYER_POSITION_ABSOLUTE]: absolutePositionAligmentConfigMap,
    [MENU_LAYER_POSITION_FIXED]: fixedPositionAligmentConfigMap,
};
const menuAlignmentContainerLayerConfigMap = {
    [MENU_ALIGNMENT_CONTAINER_LAYER_NONE]: {
        className: 'isLayerNone',
    },
    [MENU_ALIGNMENT_CONTAINER_LAYER_BASE]: {
        className: 'isLayerBase',
    },
    [MENU_ALIGNMENT_CONTAINER_LAYER_POPOVER]: {
        className: 'isLayerPopover',
    },
};
const menuBorderRadiusConfigMap = {
    [MENU_BORDER_RADIUS_MEDIUM]: {
        className: 'isRadiusMedium',
    },
    [MENU_BORDER_RADIUS_LARGE]: {
        className: 'isRadiusLarge',
    },
    [MENU_BORDER_RADIUS_EXTRA_LARGE]: {
        className: 'isRadiusExtraLarge',
    },
};
const menuItemTextOverflowConfigMap = {
    [MENU_ITEM_TEXT_OVERFLOW_NOWRAP]: {
        className: 'isTextOverflowNowrap',
    },
    [MENU_ITEM_TEXT_OVERFLOW_WRAP]: {
        className: 'isTextOverflowWrap',
    },
    [MENU_ITEM_TEXT_OVERFLOW_CLAMP]: {
        className: 'isTextOverflowClamp',
    },
};
const selectSizeConfigMap = {
    [SELECT_SIZE_SM]: {
        className: 'isSmall',
    },
    [SELECT_SIZE_MD]: {
        className: 'isMedium',
    },
};
const menuPositionConfigMap = {
    [MENU_CONTAINER_POSITION_TOP]: {
        defaultOffset: {
            top: -4,
        },
    },
    [MENU_CONTAINER_POSITION_BOTTOM]: {
        defaultOffset: {
            top: 4,
        },
    },
};

const isOptionFuzzyMatched = ({ keyword, options: { isCaseSensitive, }, value, text, isDisabled, }) => ([text, `${value}`].some((item) => {
    if (isDisabled)
        return false;
    return (isCaseSensitive
        ? item.startsWith(keyword)
        : item.toLowerCase().startsWith(keyword.toLowerCase()));
}));

const Select = ({ autoScrollToActiveItem, className, fuzzy, searchMode, containerSearchModeInputClassName, headerClassName, hiddenOptionList, isDisabled, isInvalid, isReadonly, menuAlignment = MENU_CONTAINER_ALIGNMENT_LEFT, menuAlignmentContainerLayer = MENU_ALIGNMENT_CONTAINER_LAYER_BASE, menuBorderRadius = MENU_BORDER_RADIUS_MEDIUM, menuClassName, menuCloseDelayMsec = MENU_CLOSE_DELAY_MSEC_DEFAULT, menuContainerClassName, menuFit = MENU_FIT_SELECT, menuItemTextOverflow = MENU_ITEM_TEXT_OVERFLOW_NOWRAP, menuLayerPosition = MENU_LAYER_POSITION_ABSOLUTE, menuPosition = MENU_CONTAINER_POSITION_BOTTOM, menuOffset, optionList, placeholder, size = SELECT_SIZE_MD, containerEnd, containerStart, showArrow = true, value, onChange, onMenuOpenChange, }) => {
    const selectRootElRef = useRef(null);
    const selectValueElRef = useRef(null);
    const selectFuzzySearchInputElRef = useRef(null);
    const selectMenuElRef = useRef(null);
    const selectMenuContainerRef = useRef(null);
    const selectSearchInputContainerRef = useRef(null);
    const onDocMouseClickRef = useRef(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeMenuIndex, setActiveMenuIndex,] = useState();
    const [fuzzySearchKeyword, setFuzzySearchKeyword] = useState('');
    const [searchKeyword, setSearchKeyword] = useState('');
    const { setCssVariable: setMenuContainerCssVariable, } = useCssVariable({
        targetElRef: selectMenuContainerRef,
    });
    const { text: placeholderText = SELECT_PLACEHOLDER_CONFIG_TEXT_DEFAULT, value: placeholderValue = SELECT_PLACEHOLDER_CONFIG_VALUE_DEFAULT, isOptionHidden: isPlaceholderOptionHidden = (SELECT_PLACEHOLDER_CONFIG_IS_OPTION_HIDDEN_DEFAULT), } = placeholder ?? {};
    const selectOptionList = useMemo(() => ((!placeholder || isPlaceholderOptionHidden)
        ? optionList
        : [
            {
                text: placeholderText,
                value: placeholderValue,
            },
            ...optionList,
        ]), [
        placeholder,
        placeholderText,
        placeholderValue,
        isPlaceholderOptionHidden,
        optionList,
    ]);
    const hiddenOptionMap = useMemo(() => {
        const hiddenMap = new Map();
        if (hiddenOptionList) {
            for (const { value: hiddenValue } of hiddenOptionList) {
                hiddenMap.set(hiddenValue, true);
            }
        }
        if (fuzzy && fuzzySearchKeyword) {
            const { isCaseSensitive = false, } = fuzzy;
            for (const currentOption of optionList) {
                const { text: optionText, value: optionValue, isDisabled: optionIsDisabled, } = currentOption;
                const isOptionHidden = !isOptionFuzzyMatched({
                    keyword: fuzzySearchKeyword,
                    options: {
                        isCaseSensitive,
                    },
                    text: optionText,
                    value: optionValue,
                    isDisabled: optionIsDisabled,
                });
                if (isOptionHidden) {
                    hiddenMap.set(optionValue, true);
                }
            }
        }
        return hiddenMap;
    }, [
        fuzzy,
        fuzzySearchKeyword,
        hiddenOptionList,
        optionList,
    ]);
    if (activeMenuIndex === undefined) {
        const activeOptionIndex = selectOptionList.findIndex(({ value: optionValue, }) => optionValue === value);
        setActiveMenuIndex(activeOptionIndex > -1 ? activeOptionIndex : null);
    }
    const { [menuLayerPosition]: PositonAligner, } = {
        [MENU_LAYER_POSITION_ABSOLUTE]: AbsolutePositionAligner,
        [MENU_LAYER_POSITION_FIXED]: FixedPositionAligner,
    };
    const { [menuLayerPosition]: { [menuPosition]: { [menuAlignment]: { rectCalculator: menuRectCalculator, }, }, }, } = positionAligmentConfigMap;
    const selectValueText = typeof activeMenuIndex === 'number'
        ? selectOptionList[activeMenuIndex]?.text
        : null;
    const { [menuAlignmentContainerLayer]: { className: menuAlignmentContainerLayerClassName, }, } = menuAlignmentContainerLayerConfigMap;
    const { [size]: { className: selectSizeClassName, }, } = selectSizeConfigMap;
    const { [menuItemTextOverflow]: { className: menuItemTextOverflowClassName, }, } = menuItemTextOverflowConfigMap;
    const ArrowIcon = isMenuOpen ? IosArrowUp : IosArrowDown;
    const selectMenuItemList = [];
    const { length: selectOptionListLength } = selectOptionList;
    let visibleOptionCount = 0;
    for (let i = 0; i < selectOptionListLength; i += 1) {
        const { [i]: { value: optionValue, text: optionText, isDisabled: optionIsDisabled, }, } = selectOptionList;
        const fuzzySearchMaxResults = fuzzy?.maxResults;
        const isOptionVisible = !hiddenOptionMap.get(optionValue);
        visibleOptionCount += +isOptionVisible;
        if (isOptionVisible
            && (!fuzzySearchKeyword
                || (typeof fuzzySearchMaxResults !== 'number'
                    || visibleOptionCount <= (fuzzySearchMaxResults + (placeholder ? 1 : 0))))) {
            selectMenuItemList.push(jsx(MenuItem, { contentClassName: cx(styles.textSelectOption, styles[selectSizeClassName], styles[menuItemTextOverflowClassName]), isActive: i === activeMenuIndex, isDisabled: optionIsDisabled, value: optionValue, onClick: () => {
                    if (optionIsDisabled)
                        return;
                    onChange?.(optionValue);
                    setActiveMenuIndex(i);
                }, children: optionText }, optionValue));
        }
    }
    const onSelectBoxPress = () => {
        if (isMenuOpen && !fuzzy) {
            setIsMenuOpen(false);
            onMenuOpenChange?.(false);
            return;
        }
        if (isDisabled || isReadonly) {
            return;
        }
        setIsMenuOpen(true);
        onMenuOpenChange?.(true);
        if (!autoScrollToActiveItem) {
            return;
        }
        window.setTimeout(() => {
            const { current: selectMenuEl } = selectMenuElRef;
            if (!selectMenuEl || typeof activeMenuIndex !== 'number') {
                return;
            }
            const { children: { [activeMenuIndex]: activeOptionItem, }, } = selectMenuEl;
            if (!(activeOptionItem instanceof HTMLElement)) {
                return;
            }
            selectMenuEl.scrollTo({
                top: activeOptionItem.offsetTop,
                left: 0,
            });
        }, 0);
    };
    onDocMouseClickRef.current = ({ target }) => {
        if ((target instanceof Node
            && ((selectRootElRef.current?.contains(target)
                && isMenuOpen)
                || selectSearchInputContainerRef.current?.contains(target)
                || selectFuzzySearchInputElRef.current?.isEqualNode(target)))
            || (target instanceof HTMLDivElement
                && (target.role === 'button'
                    && target.tabIndex !== undefined
                    && target.tabIndex === -1))) {
            return;
        }
        const { setTimeout, clearTimeout } = window;
        const timerId = setTimeout(() => {
            if (searchMode)
                setSearchKeyword('');
            setIsMenuOpen(false);
            onMenuOpenChange?.(false);
            clearTimeout(timerId);
        }, menuCloseDelayMsec);
    };
    useDocumentEvent({
        eventName: DOCUMENT_EVENT_NAME_CLICK,
        eventHandlerRef: onDocMouseClickRef,
    });
    useEffect(() => {
        if (typeof activeMenuIndex !== 'number') {
            return;
        }
        if (optionList[activeMenuIndex]?.value === value) {
            return;
        }
        setActiveMenuIndex(undefined);
    }, [
        activeMenuIndex,
        optionList,
        value,
    ]);
    if (!isMenuOpen && fuzzySearchKeyword) {
        setFuzzySearchKeyword('');
    }
    useEffect(() => {
        if (!isMenuOpen) {
            return;
        }
        selectFuzzySearchInputElRef.current?.focus();
    }, [isMenuOpen]);
    const handleSearchModeInputChange = ({ target, }) => {
        if (searchMode && isMenuOpen) {
            const { isCaseSensitive = false, } = searchMode;
            const matchedIndex = optionList.findIndex(({ text: optionText, value: optionValue, isDisabled: optionIsDisabled, }) => isOptionFuzzyMatched({
                keyword: target.value,
                options: {
                    isCaseSensitive,
                },
                text: optionText,
                value: optionValue,
                isDisabled: optionIsDisabled,
            }));
            if (matchedIndex === -1 || target.value === '') {
                if (target.value === '') {
                    onChange?.('');
                }
                setActiveMenuIndex(null);
            }
            else {
                onChange?.(optionList[matchedIndex].value);
                setActiveMenuIndex(matchedIndex);
            }
            setSearchKeyword(target.value);
            window.setTimeout(((matchedIdx) => {
                const { current: selectMenuEl } = selectMenuElRef;
                if (!selectMenuEl || typeof matchedIdx !== 'number') {
                    return;
                }
                const { children: { [matchedIdx]: activeOptionItem, }, } = selectMenuEl;
                if (!(activeOptionItem instanceof HTMLElement)) {
                    return;
                }
                selectMenuEl.scrollTo({
                    top: matchedIdx === -1 ? 0 : activeOptionItem.offsetTop - 100,
                    left: 0,
                });
            }).bind(null, matchedIndex), 0);
        }
    };
    return (jsx(PositonAligner, { isActive: isMenuOpen, offset: menuOffset ?? menuPositionConfigMap[menuPosition].defaultOffset, rectCalculator: menuRectCalculator, targetElement: (selectMenuItemList.length
            ? (jsxs("div", { ref: selectMenuContainerRef, className: cx(styles.containerSelectMenu, styles[selectSizeClassName], styles[menuBorderRadiusConfigMap[menuBorderRadius].className], menuContainerClassName), children: [searchMode && !(isDisabled || isReadonly) && (jsx("div", { ref: selectSearchInputContainerRef, className: cx(styles.containerSearchModeInput, containerSearchModeInputClassName), children: jsx(Input, { ref: searchMode.inputOption?.ref, alt: searchMode.inputOption?.alt, "aria-label": searchMode.inputOption?.['aria-label'], autoComplete: searchMode.inputOption?.autoComplete, className: searchMode.inputOption?.className, defaultValue: searchMode.inputOption?.defaultValue, disabled: searchMode.inputOption?.disabled, id: searchMode.inputOption?.id, inputMode: searchMode.inputOption?.inputMode, name: searchMode.inputOption?.name, pattern: searchMode.inputOption?.pattern, placeholder: searchMode.inputOption?.placeholder
                                ?? SELECT_SEARCH_MODE_INPUT_PLACEHOLDER_DEFAULT, readOnly: searchMode.inputOption?.readOnly, required: searchMode.inputOption?.required, step: searchMode.inputOption?.step, tabIndex: searchMode.inputOption?.tabIndex, type: searchMode.inputOption?.type, value: searchKeyword, onBlur: searchMode.inputOption?.onBlur, onChange: handleSearchModeInputChange, onClick: searchMode.inputOption?.onClick, onCopy: searchMode.inputOption?.onCopy, onCut: searchMode.inputOption?.onCut, onFocus: searchMode.inputOption?.onFocus, onInput: searchMode.inputOption?.onInput, onKeyDown: searchMode.inputOption?.onKeyDown, onKeyUp: searchMode.inputOption?.onKeyUp, onPaste: searchMode.inputOption?.onPaste }) })), jsx(Menu, { ref: selectMenuElRef, className: cx(styles.menuSelectPopup, searchMode && styles.searchMode, menuClassName), children: selectMenuItemList })] }))
            : jsx("div", {})), targetElementClassName: cx(styles.containerMenuAlignment, styles[menuAlignmentContainerLayerClassName]), onLocateElementWidthChange: (newLocateWidth) => {
            if (menuFit !== MENU_FIT_SELECT) {
                return;
            }
            setMenuContainerCssVariable(styles.containerSelectMenuCssVar, `${newLocateWidth}px`);
        }, children: jsx("div", { ref: selectRootElRef, className: cx(styles.selectRoot, className), role: "button", tabIndex: 0, onClick: onSelectBoxPress, onKeyDown: onSelectBoxPress, children: jsxs("div", { ref: selectValueElRef, className: cx(styles.containerSelectHead, styles[selectSizeClassName], styles[menuBorderRadiusConfigMap[menuBorderRadius].className], {
                    [styles.isDisabled]: isDisabled,
                    [styles.isInvalid]: isInvalid,
                    [styles.isReadonly]: isReadonly,
                    [styles.isDefault]: (placeholder?.value || '') === value,
                }, headerClassName), children: [(fuzzy && !(isDisabled || isReadonly))
                        ? (jsx("div", { className: styles.containerKeywordSearchInput, children: jsx(Input, { ref: selectFuzzySearchInputElRef, className: styles.inputFuzzySearch, disabled: isDisabled, isInvalid: isInvalid, placeholder: selectValueText || placeholderText, readOnly: isReadonly, value: fuzzySearchKeyword, onChange: ({ target: { value: newFuzzySearchKeyword, }, }) => {
                                    setFuzzySearchKeyword(newFuzzySearchKeyword);
                                } }) }))
                        : (jsxs("div", { className: styles.textActiveOption, children: [containerStart, (placeholder && value === placeholderValue)
                                    ? placeholderText
                                    : selectValueText || placeholderText, containerEnd] })), showArrow && (jsx("div", { className: cx(styles.containerArrowIcon, {
                            [styles.isInvalid]: isInvalid,
                        }), children: jsx(ArrowIcon, { size: SELECT_ARROW_DOWN_ICON_SIZE }) }))] }) }) }));
};

export { Select as S };
//# sourceMappingURL=component-CeHRQ8d7.js.map
